import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { BubbleMenu, type BubbleMenuProps } from '@tiptap/react/menus';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { HIGHLIGHT_COLOR, keepEditorSelection, runComposerFormatCommand } from './composerFormattingActions';
import { BulletListIcon, LinkIcon, SparkleIcon } from './composerIcons';
import { composerStyles as styles } from './composerStyles';

const BUBBLE_MENU_OPTIONS: NonNullable<BubbleMenuProps['options']> = {
	placement: 'top',
	offset: 8,
	flip: true,
	shift: { padding: 8 },
};

interface ComposerBubbleMenuProps {
	editor: Editor;
	containerRef: React.RefObject<HTMLDivElement>;
	scrollTarget: HTMLElement | null;
	/** Hide without unmounting because plugin removal terminates active mention and saved-reply sessions. */
	suppressed: boolean;
	/** AI refine-selection is only offered on customer replies. */
	showRefine: boolean;
	onOpenLink: (anchor: { x: number; y: number }) => void;
	onRefineSelection: () => void;
}

/** Selection bubble for quick B / I / U, list, highlight, link and AI refine actions. */
export const ComposerBubbleMenu: React.FC<ComposerBubbleMenuProps> = ({
	editor,
	containerRef,
	scrollTarget,
	suppressed,
	showRefine,
	onOpenLink,
	onRefineSelection,
}) => {
	const marks = useEditorState({
		editor,
		selector: ({ editor: current }) => ({
			bold: current.isActive('bold'),
			italic: current.isActive('italic'),
			underline: current.isActive('underline'),
			link: current.isActive('link'),
		}),
	});
	const appendTo = React.useCallback(() => {
		if (containerRef.current) return containerRef.current;
		// The bubble menu's debounced show can fire after the composer unmounts, and accessing
		// `editor.view` on a destroyed editor throws.
		if (editor.isDestroyed) return document.body;
		return editor.view.dom.parentElement ?? document.body;
	}, [containerRef, editor]);
	const shouldShow = React.useCallback(
		({ editor: currentEditor, from, to }: Parameters<NonNullable<BubbleMenuProps['shouldShow']>>[0]) =>
			currentEditor.isFocused && from !== to,
		[]
	);
	const options = React.useMemo<NonNullable<BubbleMenuProps['options']>>(
		() => ({
			...BUBBLE_MENU_OPTIONS,
			...(scrollTarget ? { scrollTarget } : {}),
		}),
		[scrollTarget]
	);

	const openLinkPopover = (event: React.MouseEvent<HTMLElement>) => {
		const bubbleRect = event.currentTarget.parentElement?.getBoundingClientRect();
		const containerRect = containerRef.current?.getBoundingClientRect();
		if (!bubbleRect || !containerRect) return;
		onOpenLink({
			x: bubbleRect.left - containerRect.left + bubbleRect.width / 2,
			y: bubbleRect.bottom - containerRect.top + 4,
		});
	};

	return (
		<BubbleMenu
			editor={editor}
			appendTo={appendTo}
			shouldShow={shouldShow}
			options={options}
			className={css(styles.bubbleMenu)}
			style={suppressed ? { display: 'none' } : undefined}
		>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.bubbleBtn, styles.bubbleBtnBold, ...(marks.bold ? [styles.bubbleBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'bold')}
				title='Bold'
				aria-label='Bold'
			>
				B
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.bubbleBtn, styles.bubbleBtnItalic, ...(marks.italic ? [styles.bubbleBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'italic')}
				title='Italic'
				aria-label='Italic'
			>
				I
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.bubbleBtn, styles.bubbleBtnUnderline, ...(marks.underline ? [styles.bubbleBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'underline')}
				title='Underline'
				aria-label='Underline'
			>
				U
			</Button>
			<div className={css(styles.bubbleDivider)} />
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.bubbleBtn]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'bulletList')}
				title='Bulleted list'
				aria-label='Bulleted list'
			>
				<BulletListIcon size={14} />
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.bubbleBtn]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'highlight')}
				title='Highlight'
				aria-label='Highlight'
			>
				<span className={css(styles.barGlyphStack)}>
					<span className={css(styles.barGlyphLetter)} style={{ fontSize: 12 }}>
						A
					</span>
					<span className={css(styles.barGlyphSwatch)} style={{ width: 13, background: HIGHLIGHT_COLOR }} />
				</span>
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.bubbleBtn, ...(marks.link ? [styles.bubbleBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={openLinkPopover}
				title={marks.link ? 'Edit or remove link' : 'Link'}
				aria-label={marks.link ? 'Edit or remove link' : 'Link'}
			>
				<LinkIcon size={14} />
			</Button>
			{showRefine && (
				<>
					<div className={css(styles.bubbleDivider)} />
					<Button
						variant='ghost'
						size='sm'
						styles={[styles.bubbleBtnRefine]}
						onMouseDown={keepEditorSelection}
						onClick={onRefineSelection}
						title='Refine selection with AI'
					>
						<SparkleIcon size={12} from={THEME.colors.chart.enchantment} to={THEME.colors.chart.potionPink} />
						Refine
					</Button>
				</>
			)}
		</BubbleMenu>
	);
};
